import type {
  ChatId,
  ChatMessageId,
  HubMethodContract,
  IncidentId,
  IsoDateTimeString,
} from './common';
import type { Incident } from './incident';
import type { CallNotificationItem, CallTranscript, CallTranscriptMessage } from './callTranscript';
import type {
  AttachmentBody,
  CallerConnectedStatus,
  ChatMessage,
  EmergencySituationInstructions,
  WebAppState,
} from './chat';

export type ChatMessageBody = {
  message: string | null;
  emergencySituationInstructions: EmergencySituationInstructions;
  attachment: AttachmentBody | null;
};

export type IncidentLiveState = {
  callerConnectedStatus: CallerConnectedStatus;
  webAppState: WebAppState;
  allowChatTranslations: boolean;
  callerMessagePreview: string | null;
  callerIsSendingImage: boolean;
  dispatcherMessagePreview: string | null;
  dispatcherIsSendingImage: boolean;
};

export type PortalHubClientEvents = {
  IncidentCreated: (incident: Incident) => void;
  IncidentChanged: (incident: Incident) => void;
  IncidentClosed: (incident: Incident) => void;
  CallNotificationCreated: (callNotification: CallNotificationItem) => void;
  CallNotificationChanged: (callNotification: CallNotificationItem) => void;
  CallNotificationEnded: (callNotification: CallNotificationItem) => void;
  ChatActivated: (incidentId: IncidentId, chatId: ChatId) => void;
  NewMessageReceived: (incidentId: IncidentId, message: ChatMessage) => void;
  MessageReceiveConfirmed: (
    incidentId: IncidentId,
    messageId: ChatMessageId,
    confirmationTime: IsoDateTimeString,
  ) => void;
  CallerChatTextMessagePreviewReceived: (incidentId: IncidentId, messagePreview: string | null) => void;
  CallerChatImageMessagePreviewReceived: (incidentId: IncidentId, isSendingImage: boolean) => void;
  DispatcherChatTextMessagePreviewReceived: (incidentId: IncidentId, messagePreview: string | null) => void;
  DispatcherChatImageMessagePreviewReceived: (incidentId: IncidentId, isSendingImage: boolean) => void;
  ChatTranslationsAllowedChanged: (incidentId: IncidentId, allowChatTranslations: boolean) => void;
  WebAppStateChanged: (incidentId: IncidentId, webAppState: WebAppState) => void;
  CallerConnectionStatusChanged: (incidentId: IncidentId, callerConnectedStatus: CallerConnectedStatus) => void;
  IncidentCallStarted: (incidentId: IncidentId, callNotification: CallNotificationItem) => void;
  IncidentCallEnded: (incidentId: IncidentId, endTimeUtc: IsoDateTimeString) => void;
  TranscriptInitialized: (incidentId: IncidentId, transcript: CallTranscript) => void;
  TranscriptMessageUpdated: (incidentId: IncidentId, message: CallTranscriptMessage) => void;
  TranscriptUpdated: (incidentId: IncidentId, transcript: CallTranscript) => void;
};

export const PORTAL_HUB_CLIENT_EVENTS = {
  IncidentCreated: { params: ['PortalIncident'], returns: 'Task' },
  IncidentChanged: { params: ['PortalIncident'], returns: 'Task' },
  IncidentClosed: { params: ['PortalIncident'], returns: 'Task' },
  CallNotificationCreated: { params: ['CallNotificationItem'], returns: 'Task' },
  CallNotificationChanged: { params: ['CallNotificationItem'], returns: 'Task' },
  CallNotificationEnded: { params: ['CallNotificationItem'], returns: 'Task' },
  ChatActivated: { params: ['IncidentId', 'ChatId'], returns: 'Task' },
  NewMessageReceived: { params: ['IncidentId', 'ChatMessage'], returns: 'Task' },
  MessageReceiveConfirmed: { params: ['IncidentId', 'ChatMessageId', 'DateTimeOffset'], returns: 'Task' },
  CallerChatTextMessagePreviewReceived: { params: ['IncidentId', 'string?'], returns: 'Task' },
  CallerChatImageMessagePreviewReceived: { params: ['IncidentId', 'bool'], returns: 'Task' },
  DispatcherChatTextMessagePreviewReceived: { params: ['IncidentId', 'string?'], returns: 'Task' },
  DispatcherChatImageMessagePreviewReceived: { params: ['IncidentId', 'bool'], returns: 'Task' },
  ChatTranslationsAllowedChanged: { params: ['IncidentId', 'bool'], returns: 'Task' },
  WebAppStateChanged: { params: ['IncidentId', 'WebAppState'], returns: 'Task' },
  CallerConnectionStatusChanged: { params: ['IncidentId', 'CallerConnectedStatus'], returns: 'Task' },
  IncidentCallStarted: { params: ['IncidentId', 'CallNotificationItem'], returns: 'Task' },
  IncidentCallEnded: { params: ['IncidentId', 'DateTime'], returns: 'Task' },
  TranscriptInitialized: { params: ['IncidentId', 'CallTranscript'], returns: 'Task' },
  TranscriptMessageUpdated: { params: ['IncidentId', 'CallTranscriptMessage'], returns: 'Task' },
  TranscriptUpdated: { params: ['IncidentId', 'CallTranscript'], returns: 'Task' },
} as const satisfies Record<keyof PortalHubClientEvents, HubMethodContract>;

export type PortalHubServerMethods = {
  GetLiveIncidents: () => Promise<Incident[]>;
  SubscribeToIncident: (incidentId: IncidentId) => Promise<void>;
  UnsubscribeFromIncident: (incidentId: IncidentId) => Promise<void>;
  GetIncidentLiveState: (incidentId: IncidentId) => Promise<IncidentLiveState>;
  GetIncidentCall: (incidentId: IncidentId) => Promise<CallNotificationItem | null>;
  SetIsChatTranslationsAllowed: (incidentId: IncidentId, allowChatTranslations: boolean) => Promise<void>;
  SendChatMessage: (incidentId: IncidentId, body: ChatMessageBody) => Promise<void>;
  SendDispatcherChatTextMessagePreview: (incidentId: IncidentId, messagePreview: string) => Promise<void>;
  SendDispatcherChatImageMessagePreview: (incidentId: IncidentId, isSendingImage: boolean) => Promise<void>;
  EndLiveVideo: (incidentId: IncidentId) => Promise<void>;
  SwitchCameraRequest: (incidentId: IncidentId) => Promise<void>;
  TakeCallerCameraSnapshot: (incidentId: IncidentId) => Promise<void>;
  MuteRequest: (incidentId: IncidentId) => Promise<void>;
  UnmuteRequest: (incidentId: IncidentId) => Promise<void>;
};

export const PORTAL_HUB_SERVER_METHODS = {
  GetLiveIncidents: { params: [], returns: 'Task<PortalIncident[]>' },
  SubscribeToIncident: { params: ['IncidentId'], returns: 'Task' },
  UnsubscribeFromIncident: { params: ['IncidentId'], returns: 'Task' },
  GetIncidentLiveState: { params: ['IncidentId'], returns: 'Task<IncidentLiveState>' },
  GetIncidentCall: { params: ['IncidentId'], returns: 'Task<CallNotificationItem?>' },
  SetIsChatTranslationsAllowed: { params: ['IncidentId', 'bool'], returns: 'Task' },
  SendChatMessage: { params: ['IncidentId', 'ChatMessageBody'], returns: 'Task' },
  SendDispatcherChatTextMessagePreview: { params: ['IncidentId', 'string'], returns: 'Task' },
  SendDispatcherChatImageMessagePreview: { params: ['IncidentId', 'bool'], returns: 'Task' },
  EndLiveVideo: { params: ['IncidentId'], returns: 'Task' },
  SwitchCameraRequest: { params: ['IncidentId'], returns: 'Task' },
  TakeCallerCameraSnapshot: { params: ['IncidentId'], returns: 'Task' },
  MuteRequest: { params: ['IncidentId'], returns: 'Task' },
  UnmuteRequest: { params: ['IncidentId'], returns: 'Task' },
} as const satisfies Record<keyof PortalHubServerMethods, HubMethodContract>;
